// SPDX-License-Identifier: BUSL-1.1

/**
 * Agent detail page with unified Activity view.
 *
 * Shows sessions, jobs, or both based on agent capabilities.
 * Capabilities are defined per-agent:
 *   - sessions: true  → Interactive sessions tab
 *   - jobs: true     → Jobs tab
 *   - Both true     → Both tabs shown
 *
 * Page is the tab orchestrator + data fetching only. All presentation lives
 * in `@/components/agent-detail/*` (extracted in the P3b refactor).
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router';
import { AgentDetailSkeleton } from '@/components/skeletons';
import { AgentBudgetCard } from '@/components/agent-budget-card';
import { FleetActivityStreamProvider } from '@/components/live-activity-cell';
import { useCurrentOrg } from '@/contexts/org-context';
import {
  ActivityTab,
  AgentDetailHeader,
  AgentDetailMetadataPanel,
  AgentDetailTabBar,
  EventsTab,
  JobDetailSheet,
  JobsTab,
  SessionsTab,
  useAgentDetailQueries,
  type ActivityItem,
  type Job,
  type TabId,
} from '@/components/agent-detail';

export function AgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [streamSessionId, setStreamSessionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('activity');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  // NocoDB #4790: scope the page in FleetActivityStreamProvider so the
  // JobDetailSheet can subscribe to the org-wide activity stream and
  // light up live as the agent processes a job. Single SSE connection
  // shared with any other consumer mounted on this page (current
  // consumers: JobDetailSheet only — same pattern as /operators).
  const { currentOrg } = useCurrentOrg();

  const { agent, anthropicAgent, sessions, jobs, events, terminate } =
    useAgentDetailQueries(id);

  const isLoading = agent.isLoading || anthropicAgent.isLoading || sessions.isLoading;
  if (isLoading) return <AgentDetailSkeleton />;

  // Use unified agent data if available, otherwise fallback to Anthropic
  const agentData = agent.data?.data ?? anthropicAgent.data;
  const sessionData = sessions.data?.data ?? [];
  const jobData = jobs.data?.data ?? [];
  const eventData = events.data?.events ?? [];

  if (!agentData) {
    return (
      <div>
        <Link to="/agents" className="text-sm text-blue-400 hover:underline">
          &larr; Back to Agents
        </Link>
        <p className="mt-4 text-neutral-500">Agent not found.</p>
      </div>
    );
  }

  // Determine capabilities + which tabs to show
  const capabilities = agentData.capabilities ?? {
    sessions: sessionData.length > 0,
    jobs: false,
  };
  const showSessions = capabilities.sessions !== false && sessionData.length > 0;
  const showJobs = capabilities.jobs === true || (agentData.source === 'runner');
  // HB-03 events tab is shown whenever the agent has emitted at least one
  // activity event. New agents start with zero events, so the tab stays
  // hidden until they actually do work.
  const showEvents = eventData.length > 0;

  // Set default tab based on capabilities
  const defaultTab: TabId =
    (!showJobs && showSessions) ? 'sessions' :
    (showJobs && !showSessions) ? 'jobs' : 'activity';

  // Update tab if current tab isn't available
  if (!['sessions', 'jobs', 'activity', 'events'].includes(activeTab) ||
      (activeTab === 'sessions' && !showSessions) ||
      (activeTab === 'jobs' && !showJobs) ||
      (activeTab === 'events' && !showEvents)) {
    setActiveTab(defaultTab);
  }

  // Normalize agent data for display
  const displayName = agentData.name ?? agentData.agent_id ?? id;
  const displayStatus = agentData.status ?? 'unknown';
  const displayModel = agentData.model ?? agentData.config?.model;
  const displaySdk = agentData.sdk ?? agentData.framework;

  // Build unified activity data
  const activityData: ActivityItem[] = [
    ...sessionData.map((s) => ({ ...s, type: 'session' as const })),
    ...jobData.map((j) => ({ ...j, type: 'job' as const })),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <FleetActivityStreamProvider orgId={currentOrg?.id ?? null}>
    <div>
      <Link
        to="/agents"
        className="mb-4 inline-block text-sm text-blue-400 hover:underline"
      >
        &larr; Back to Agents
      </Link>

      <AgentDetailHeader
        displayName={displayName}
        displayStatus={displayStatus}
        displayModel={displayModel}
        displaySdk={displaySdk}
        showSessions={showSessions}
        onTerminate={() => {
          const runningIds = sessionData
            .filter((s) => s.status === 'running')
            .map((s) => s.id);
          terminate.mutate(runningIds);
        }}
        terminatePending={terminate.isPending}
      />

      <AgentBudgetCard agent={agentData} agentId={id} />

      <AgentDetailMetadataPanel agentData={agentData} agentId={id} />

      <AgentDetailTabBar
        activeTab={activeTab}
        onSelect={setActiveTab}
        showSessions={showSessions}
        showJobs={showJobs}
        showEvents={showEvents}
        sessionCount={sessionData.length}
        jobCount={jobData.length}
        eventCount={eventData.length}
        hasMoreEvents={!!events.data?.nextCursor}
      />

      {activeTab === 'activity' && (
        <ActivityTab
          activityData={activityData}
          onSelectJob={setSelectedJob}
          onSelectRunningSession={(sessionId) => {
            setActiveTab('sessions');
            setStreamSessionId(sessionId);
          }}
        />
      )}

      {activeTab === 'sessions' && (
        <SessionsTab
          sessionData={sessionData}
          streamSessionId={streamSessionId}
          setStreamSessionId={setStreamSessionId}
          terminate={terminate}
          displayName={displayName}
        />
      )}

      {activeTab === 'jobs' && (
        <JobsTab jobData={jobData} onJobClick={setSelectedJob} />
      )}

      {activeTab === 'events' && (
        <EventsTab
          eventData={eventData}
          isLoading={events.isLoading}
          hasNextCursor={!!events.data?.nextCursor}
        />
      )}

      <JobDetailSheet
        job={selectedJob}
        jobList={jobData}
        open={selectedJob !== null}
        onClose={() => setSelectedJob(null)}
        onSelectJob={setSelectedJob}
        onTaskCompleted={() => {
          // task_completed event arrived for the open job — invalidate
          // the jobs query so the modal re-renders with the final
          // status, outputPayload, and cost values. Idempotent: react-
          // query coalesces concurrent refetches.
          jobs.refetch();
        }}
      />
    </div>
    </FleetActivityStreamProvider>
  );
}
